import { useMemo } from 'react';
import { DAY_NAMES } from '../types';
import type { EventInstance } from '../types';
import { buildMonthGrid } from '../utils';
import { MonthCell } from './MonthCell';

interface MonthViewProps {
  currentDate: Date;
  events: EventInstance[];
  weekStartDay: number;
  onClickDate: (date: Date) => void;
  onClickEvent: (event: EventInstance) => void;
  showWeekends?: boolean;
  monthViewMaxEvents?: number;
  showEventTimesInMonthView?: boolean;
  dimPastEvents?: boolean;
  eventColorPriority?: 'event' | 'calendar';
}

export function MonthView({
  currentDate,
  events,
  weekStartDay,
  onClickDate,
  onClickEvent,
  showWeekends = true,
  monthViewMaxEvents = 3,
  showEventTimesInMonthView = false,
  dimPastEvents = false,
  eventColorPriority = 'event',
}: MonthViewProps) {
  const weeks = buildMonthGrid(currentDate, events, weekStartDay);

  // Weekend column indices (relative to weekStartDay ordering)
  const weekendIndices = useMemo(() => {
    if (showWeekends) return null;
    const indices: number[] = [];
    for (let i = 0; i < 7; i++) {
      const dayIndex = (weekStartDay + i) % 7;
      if (dayIndex === 0 || dayIndex === 6) indices.push(i);
    }
    return indices;
  }, [weekStartDay, showWeekends]);

  const dayHeaders = [];
  for (let i = 0; i < 7; i++) {
    const dayIndex = (weekStartDay + i) % 7;
    if (weekendIndices?.includes(i)) continue;
    dayHeaders.push(DAY_NAMES[dayIndex]);
  }

  const colCount = dayHeaders.length;

  return (
    <div className="flex flex-col flex-1 overflow-auto">
      {/* Day-of-week header */}
      <div className="grid border-b" style={{ gridTemplateColumns: `repeat(${colCount}, 1fr)` }}>
        {dayHeaders.map((name) => (
          <div
            key={name}
            className="text-center text-xs font-medium text-muted-foreground py-2 border-r last:border-r-0"
          >
            {name}
          </div>
        ))}
      </div>

      {/* Week rows */}
      <div className="flex-1">
        {weeks.map((week, wi) => {
          const cells = weekendIndices
            ? week.filter((_, di) => !weekendIndices.includes(di))
            : week;

          return (
            <div
              key={wi}
              className="grid"
              style={{ gridTemplateColumns: `repeat(${colCount}, 1fr)` }}
            >
              {cells.map((cell, di) => (
                <MonthCell
                  key={di}
                  date={cell.date}
                  isCurrentMonth={cell.isCurrentMonth}
                  isToday={cell.isToday}
                  events={cell.events}
                  maxVisible={monthViewMaxEvents === 0 ? Infinity : monthViewMaxEvents}
                  showEventTimes={showEventTimesInMonthView}
                  onClickDate={onClickDate}
                  onClickEvent={onClickEvent}
                  dimPastEvents={dimPastEvents}
                  eventColorPriority={eventColorPriority}
                />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
